import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, map } from 'rxjs';

import environment from '@environments/environment';

import {
  ListOrganizationsParams,
  ListOrganizationTypesParams,
  OrganizationAddressesResponseBody,
  OrganizationDefaultParams,
  OrganizationResponseBody,
  OrganizationsResponseBody,
  OrganizationTypesResponseBody,
  Paged,
  ResponseModel,
} from '@erm/cluster';
import { ORGANIZATION_URL } from '@data-access/shared';
import {
  CreateOrganizationAdminPayload,
  OrganizationAdminMember,
  OrganizationAdminResult,
  OrganizationAffiliationResponse,
  OrganizationContact,
  OrganizationContactData,
} from '../types';
import { WcgMenuOption } from '@wcg/frontend-framework';

const _defaultParams: OrganizationDefaultParams = {
  page: 1,
  size: 10,
};

@Injectable()
export class OrganizationsV2Service {
  private readonly _http = inject(HttpClient);
  private readonly _orgApiUrl = `${environment.jemmaApi}api/v1/secure/`;
  /**
   * @NOTE :- ORGANIZATIONS V2
   *
   */
  /**
   * Returns a paginated list of all existing organizations.
   *
   * @param params        Optional query parameters for filtering / sorting / paging.
   * @param onBehalfOf    Optional value for the `jemma-on-behalf-of` header.
   */
  listOrganizations(
    params: ListOrganizationsParams = {},
    onBehalfOf?: string,
  ): Observable<ResponseModel<Paged<OrganizationsResponseBody>>> {
    const url = `${this._orgApiUrl}organizations`;
    return this._http.get<ResponseModel<Paged<OrganizationsResponseBody>>>(
      url,
      {
        headers: this._buildHeaders(onBehalfOf),
        params: this._toOrganizationsHttpParams(params),
      },
    );
  }

  /**
   * Returns a paginated list of all existing organization types.
   *
   * @param params        Optional query parameters for filtering / sorting / paging.
   * @param onBehalfOf    Optional value for the `jemma-on-behalf-of` header.
   */
  listOrganizationTypes(
    params: ListOrganizationTypesParams = {},
    onBehalfOf?: string,
  ): Observable<ResponseModel<Paged<OrganizationTypesResponseBody>>> {
    const _url = `${this._orgApiUrl}organization-types`;
    return this._http.get<ResponseModel<Paged<OrganizationTypesResponseBody>>>(
      _url,
      {
        headers: this._buildHeaders(onBehalfOf),
        params: this._toOrganizationTypesHttpParams(params),
      },
    );
  }

  /*
   * @Fetch Organization
   * */
  fetchOrg(organization_key: string) {
    const _url = `${this._orgApiUrl}organizations/${organization_key}`;
    return this._http.get<ResponseModel<OrganizationResponseBody>>(_url, {
      headers: this._buildHeaders(),
    });
  }

  fetchOrgAddresses(organization_key: string, params = _defaultParams) {
    const _url = `${this._orgApiUrl}organizations/${organization_key}/addresses`;
    return this._http.get<
      ResponseModel<Paged<OrganizationAddressesResponseBody>>
    >(_url, {
      headers: this._buildHeaders(),
      params: this._toOrganizationsHttpParams(params),
    });
  }

  fetchOrganizationAdmins(organization_key: string) {
    const _url = `${ORGANIZATION_URL}${organization_key}/members/`;
    return this._http.get<OrganizationAdminMember[]>(_url);
  }

  loadOrganizationRoles = (url: string) => {
    return this._http.get<WcgMenuOption[]>(`${url}`);
  };

  loadOrganizationContact = (url: string) => {
    return this._http
      .get<OrganizationAffiliationResponse[]>(`${url}`)
      .pipe(
        map((affiliations) =>
          this._transformAffiliationsToContacts(affiliations),
        ),
      );
  };

  addOrganizationContact = (url: string, data: OrganizationContactData) => {
    return this._http.post<OrganizationContact>(
      `${url}`,
      this._toOrganizationContactRequestBody(data),
    );
  };

  updateOrganizationContact = (url: string, data: OrganizationContactData) => {
    return this._http.put<OrganizationContact>(
      `${url}`,
      this._toOrganizationContactRequestBody(data),
    );
  };

  removeOrganizationContact = (url: string) => {
    return this._http.delete(`${url}`);
  };

  private _toOrganizationContactRequestBody(
    data: OrganizationContactData,
  ): Omit<OrganizationContactData, 'organization_role'> & {
    organization_role: string;
  } {
    return {
      ...data,
      organization_role:
        data.organization_role.value ?? data.organization_role.label,
    };
  }

  addOrganizationAdmin(
    organization_key: string,
    payload: CreateOrganizationAdminPayload,
  ) {
    const _url = `${ORGANIZATION_URL}${organization_key}/members/`;
    return this._http.post<OrganizationAdminResult>(_url, payload);
  }

  removeOrganizationAdmin(organization_key: string, user_id: string) {
    const _url = `${ORGANIZATION_URL}${organization_key}/members/${user_id}/`;
    return this._http.delete<void>(_url);
  }

  private _transformAffiliationsToContacts(
    affiliations: OrganizationAffiliationResponse[],
  ): OrganizationContact[] {
    return affiliations.map((affiliation) => ({
      id: affiliation.relationship_id,
      organization_id: affiliation.organization_id,
      organization_name: null,
      organization_role: affiliation.label,
      relationship_id: affiliation.relationship_id,
      organization_source: affiliation.organization_source,
      admins: affiliation.admins,
    }));
  }

  private _buildHeaders(onBehalfOf?: string): HttpHeaders {
    let headers = new HttpHeaders({
      'jemma-event-source': environment.jemmaEventSource,
    });

    if (onBehalfOf) {
      headers = headers.set('jemma-on-behalf-of', onBehalfOf);
    }

    return headers;
  }

  private _toOrganizationsHttpParams(
    params: ListOrganizationsParams,
  ): HttpParams {
    let p = new HttpParams();

    if (params.page != null) p = p.set('page', params.page);
    if (params.size != null) p = p.set('size', params.size);
    if (params.organization_key)
      p = p.set('organization_key', params.organization_key);
    params.organization_keys?.forEach(
      (k) => (p = p.append('organization_keys', k)),
    );
    if (params.organization_name)
      p = p.set('organization_name', params.organization_name);
    if (params.organization_name__ilike)
      p = p.set('organization_name__ilike', params.organization_name__ilike);
    if (params.organization_name__like)
      p = p.set('organization_name__like', params.organization_name__like);
    if (params.organization_type_key)
      p = p.set('organization_type_key', params.organization_type_key);
    params.organization_type_keys?.forEach(
      (k) => (p = p.append('organization_type_keys', k)),
    );
    if (params.primary_organization_type)
      p = p.set('primary_organization_type', params.primary_organization_type);
    params.primary_organization_type_name?.forEach(
      (n) => (p = p.append('primary_organization_type_name', n)),
    );
    if (params.status) p = p.set('status', params.status);
    if (params.order_by) {
      p = p.set('order_by', params.order_by);
    }

    return p;
  }

  private _toOrganizationTypesHttpParams(
    params: ListOrganizationTypesParams,
  ): HttpParams {
    let p = new HttpParams();

    if (params.page != null) p = p.set('page', params.page);
    if (params.size != null) p = p.set('size', params.size);
    if (params.organization_type_name)
      p = p.set('organization_type_name', params.organization_type_name);
    params.organization_type_names?.forEach(
      (n) => (p = p.append('organization_type_names', n)),
    );
    if (params.organization_type_name__ilike)
      p = p.set(
        'organization_type_name__ilike',
        params.organization_type_name__ilike,
      );
    if (params.organization_type_name__like)
      p = p.set(
        'organization_type_name__like',
        params.organization_type_name__like,
      );
    if (params.order_by) {
      p = p.set('order_by', params.order_by);
    }
    return p;
  }
}
